import { FC, Dispatch, SetStateAction } from 'react';

import {
  Header,
  Button,
  SpaceBetween,
  Table,
  Flashbar,
  Box,
  TextFilter,
  Pagination
} from '@awsui/components-react';
import { useCollection } from '@awsui/collection-hooks';

import { FlashbarProps } from '@awsui/components-react';
import { IHomeGroup } from '../../../../../interfaces/domain/homegroup';

import CreateHomeGroupForm from '../createHomeGroupForm';
import EditHomeGroupForm from '../editHomeGroupForm';
import ChildModal from '../../../../common/ChildModal';

interface ViewProps {
  homeGroups: IHomeGroup[];
  selectedHomeGroups: IHomeGroup[];
  flashBarItems: FlashbarProps.MessageDefinition[];
  setFlashBarItems: Dispatch<SetStateAction<FlashbarProps.MessageDefinition[]>>;
  disableActionButtons: boolean;
  handleTableRowSelection: (homeGroup: IHomeGroup[]) => void;
  homeGroupsIsEmpty: () => boolean;
  tableIsLoading: boolean;
  createHomeGroupFormModalIsVisible: boolean;
  setCreateHomeGroupFormModalIsVisible: Dispatch<SetStateAction<boolean>>;
  editHomeGroupFormModalIsVisible: boolean;
  setEditHomeGroupFormModalIsVisible: Dispatch<SetStateAction<boolean>>;
  refreshHomeGroupTable: () => void;
}

const View: FC<ViewProps> = ({
  homeGroups,
  selectedHomeGroups,
  flashBarItems,
  setFlashBarItems,
  handleTableRowSelection,
  disableActionButtons,
  homeGroupsIsEmpty,
  tableIsLoading,
  createHomeGroupFormModalIsVisible,
  setCreateHomeGroupFormModalIsVisible,
  editHomeGroupFormModalIsVisible,
  setEditHomeGroupFormModalIsVisible,
  refreshHomeGroupTable
}) => {
  const renderEmptyTableContent = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No Home Groups</b>
        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
          No Home Groups to display.
        </Box>
        {
          <Button
            variant="primary"
            iconName="add-plus"
            onClick={() => {
              setCreateHomeGroupFormModalIsVisible(true);
            }}
          >
            Create Home Group
          </Button>
        }
      </Box>
    );
  };
  const renderEmptyTableContentAfterFiltering = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No matching Home Groups</b>
      </Box>
    );
  };

  const {
    items,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps
  } = useCollection(homeGroups, {
    filtering: {
      empty: renderEmptyTableContent(),
      noMatch: renderEmptyTableContentAfterFiltering(),
      fields: ['groupName', 'emailDomain']
    },
    pagination: { pageSize: 10 },
    sorting: {},
    selection: {}
  });
  const { selectedItems: filteredHomeGroups = [] } = collectionProps;

  const renderTableActions = () => {
    return (
      <>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="refresh"
            variant="link"
            onClick={() => refreshHomeGroupTable()}
          />

          <Button
            iconName="add-plus"
            disabled={homeGroupsIsEmpty()}
            onClick={() => {
              setCreateHomeGroupFormModalIsVisible(true);
            }}
          >
            Create
          </Button>
          <Button
            iconName="settings"
            disabled={disableActionButtons}
            onClick={() => {
              setEditHomeGroupFormModalIsVisible(true);
            }}
          >
            Edit
          </Button>
        </SpaceBetween>
      </>
    );
  };
  const renderTableHeader = () => {
    return (
      <Header
        counter={
          filteredHomeGroups.length
            ? `(${filteredHomeGroups.length}/${homeGroups.length})`
            : `(${homeGroups.length})`
        }
        actions={renderTableActions()}
      >
        Home Groups
      </Header>
    );
  };

  const renderCreateHomeGroupFormModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          handleTableRowSelection([]);
          setCreateHomeGroupFormModalIsVisible(false);
        }}
        visible={createHomeGroupFormModalIsVisible}
        closeAriaLabel="Close modal"
        size="medium"
        header="New Home Group"
      >
        <CreateHomeGroupForm refreshHomeGroupTable={refreshHomeGroupTable} />
      </ChildModal>
    );
  };

  const renderEditHomeGroupFormModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setEditHomeGroupFormModalIsVisible(false);
        }}
        visible={editHomeGroupFormModalIsVisible}
        closeAriaLabel="Close modal"
        size="medium"
        header="Edit Home Group"
      >
        <EditHomeGroupForm
          refreshHomeGroupTable={refreshHomeGroupTable}
          homeGroups={selectedHomeGroups}
        />
      </ChildModal>
    );
  };

  const tableColumnDefinitions = [
    {
      id: 'groupName',
      header: 'Name',
      cell: (item: IHomeGroup) => item.groupName || '-'
    },
    {
      id: 'emailDomain',
      header: 'Email Domain',
      cell: (item: IHomeGroup) => item.emailDomain || '-'
    },
    {
      id: 'adDirectories',
      header: 'Directory IDs',
      cell: (item: IHomeGroup) =>
        item.adDirectories.map((adDirectory) => adDirectory.alias).join(', ') ||
        '-'
    },
    {
      id: 'bundles',
      header: 'Bundles',
      cell: (item: IHomeGroup) =>
        item.bundles.map((bundle) => bundle.bundleId).join(', ') || '-'
    }
  ];
  return (
    <>
      <SpaceBetween direction="vertical" size="xs">
        <Flashbar items={flashBarItems} />
        <Table
          {...collectionProps}
          loading={tableIsLoading}
          trackBy="groupName"
          selectedItems={selectedHomeGroups}
          selectionType="single"
          columnDefinitions={tableColumnDefinitions}
          items={items}
          loadingText="Loading Home Groups"
          onSelectionChange={({ detail }) =>
            handleTableRowSelection(detail.selectedItems)
          }
          header={renderTableHeader()}
          filter={
            <TextFilter
              {...filterProps}
              countText={`${filteredItemsCount} matches`}
              filteringAriaLabel="Filter Home Groups"
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: 'Next page',
                previousPageLabel: 'Previous page',
                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`
              }}
            />
          }
        />
      </SpaceBetween>
      {renderCreateHomeGroupFormModal()}
      {renderEditHomeGroupFormModal()}
    </>
  );
};
export default View;
